import React, {useState, useEffect, useCallback} from "react";
import axios from '../../../api/axios';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Snackbar,
  Stack,
  Pagination,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  ContactEmergency,
  LockReset as ResetPasswordIcon,
  PersonOff as DisableIcon,
  Delete as DeleteIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Clear as ClearIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import useData from '../../../Hooks/useData';
import styles from './Users.module.css';

// Get API base URL for serving images
const getApiBaseUrl = () => {
  // In production, use the deployed API URL
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return 'https://baptistmenapi.onrender.com';
  }
  // In development, use localhost
  return 'http://localhost:3001';
};



const path = '/api/users';

const Users = () => {
  const { auth } = useData();
  const token = auth?.token;
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', user: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sortModel, setSortModel] = useState([]); // empty = use backend default (UserEnabled desc, FullName asc)

// DataGrid column ids don't always match DB columns (e.g. 'name' is composed
// from FirstName/LastName in the UI, but the sortable backing column is FullName)
const sortFieldMap = { name: 'FullName', email: 'Email', status: 'UserEnabled' };
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    totalCount: 0,
    totalPages: 0
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // MUI DataGrid uses 0-based indexing
    pageSize: 25,
  });
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");

  // Fetch users data with pagination
  const fetchUsersData = useCallback(async (page = 1, limit = 25, search = '', sort = []) => {
  setIsLoading(true);
  setError(null);
  try {
    const activeSort = sort?.[0];
    const backendSortField = activeSort ? sortFieldMap[activeSort.field] : undefined;

    // Build query parameters
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
      ...(backendSortField && { sortField: backendSortField, sortOrder: activeSort.sort })
    });

    const requestConfig = {
      withCredentials: true
    };

    if (token) {
      requestConfig.headers = { Authorization: `Bearer ${token}` };
    }

    const result = await axios.get(`${path}?${params}`, requestConfig);

    const responseData = result.data;
    setData(responseData.users || []);
    setPagination(responseData.pagination || {
      page: 1,
      limit: 25,
      totalCount: 0,
      totalPages: 0
    });
  } catch (error) {
    setError("Failed to fetch users. Please try again or contact an admin.");
  } finally {
    setIsLoading(false);
  }
}, [token]);

  // Initial data fetch
useEffect(() => {
  fetchUsersData(1, 25, '', sortModel);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

// Handle pagination changes
const handlePaginationModelChange = useCallback((newModel) => {
  setPaginationModel(newModel);
  fetchUsersData(newModel.page + 1, newModel.pageSize, searchTerm, sortModel);
}, [fetchUsersData, searchTerm, sortModel]);

// Handle sort changes
const handleSortModelChange = useCallback((newSortModel) => {
  setSortModel(newSortModel);
  setPaginationModel(prev => ({ ...prev, page: 0 }));
  fetchUsersData(1, paginationModel.pageSize, searchTerm, newSortModel);
}, [fetchUsersData, paginationModel.pageSize, searchTerm]);

  const handleSearch = () => {
  setPaginationModel(prev => ({ ...prev, page: 0 }));
  fetchUsersData(1, paginationModel.pageSize, searchTerm, sortModel);
};

const handleClearSearch = () => {
  setSearchTerm('');
  setPaginationModel(prev => ({ ...prev, page: 0 }));
  fetchUsersData(1, paginationModel.pageSize, '', sortModel);
};

  // Handle Enter key press in search field
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Removed auto-reload on tab focus to prevent unnecessary data fetching when switching tabs

  // Handle user actions
  const handleEditUser = (userId) => {
    navigate(`/admin/users/edit/${userId}`);
  };

  const handleEditUserProfile = (userId)=> {
    navigate(`/admin/users/edit/profile/${userId}`);
  }

  const handleResetPassword = (user) => {
    setConfirmDialog({
      open: true,
      action: 'resetPassword',
      user: user
    });
  };

  const handleDisableUser = (user) => {
    setConfirmDialog({
      open: true,
      action: 'disable',
      user: user
    });
  };

  const handleDeleteUser = (user) => {
    setConfirmDialog({
      open: true,
      action: 'delete',
      user: user
    });
  };

  const handleConfirmAction = async () => {
    const { action, user } = confirmDialog;
    setConfirmDialog({ open: false, action: '', user: null });
    
    try {
      // Implement API calls for different actions
      switch (action) {
        case 'resetPassword':
          await handlePasswordResetConfirm(user);
          break;
        case 'disable':
          await handleDisableUserConfirm(user);
          break;
        case 'delete':
          await handleDeleteUserConfirm(user);
          break;
        default:
          break;
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message || "Action failed. Please try again.");
    }
  };

  const handlePasswordResetConfirm = async (user) => {
    try {
      setIsLoading(true);
      
      // Send password reset request to backend
      const data = {
        email: user.Email || user.UserName,
        resetType: 'admin-reset',
        userId: user.UserID
      };
      
      const response = await axios.post('/api/auth/forgot-password', JSON.stringify(data), {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      
      // Show success message
      setError(null);
      setSnackbar({
        open: true,
        message: `Password reset link sent successfully to ${user.Email || user.UserName}`,
        severity: 'success'
      });
      
    } catch (error) {
      // Handle specific error cases
      let errorMessage = "Failed to send password reset email. Please try again.";
      
      if (error.response?.status === 404) {
        errorMessage = "User email not found. Please ensure the user has a valid email address.";
      } else if (error.response?.status === 429) {
        errorMessage = "Too many reset requests. Please wait a few minutes before trying again.";
      } else if (error.response?.status >= 500) {
        errorMessage = "Server error occurred. Please try again later.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableUserConfirm = async (user) => {
    try {
      setIsLoading(true);
      
      // Update user enabled status - only send the field that needs to be updated
      // Don't send nested relations like CMS_UserRole
      const updatedUser = {
        UserEnabled: !user.UserEnabled,
        UserLastModified: new Date()
      };
      
      await axios.put(`/api/users/${user.UserID}`, updatedUser, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });

      // Refresh the users list
      await fetchUsersData(pagination.page, pagination.limit, searchTerm);
      
      setSnackbar({
        open: true,
        message: `User ${user.UserEnabled ? 'disabled' : 'enabled'} successfully`,
        severity: 'success'
      });
      
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to update user status. Please try again.",
        severity: 'error'
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUserConfirm = async (user) => {
    try {
      setIsLoading(true);
      
      await axios.delete(`/api/users/${user.UserID}`);

      // Refresh the users list
      await fetchUsersData(pagination.page, pagination.limit, searchTerm);
      
      setSnackbar({
        open: true,
        message: 'User deleted successfully',
        severity: 'success'
      });
      
    } catch (error) {
      let errorMessage = "Failed to delete user. Please try again.";
      
      if (error.response?.status === 400) {
        // Foreign key constraint or dependency issue
        errorMessage = error.response.data.error || "Cannot delete user: User has associated records. Please disable the user instead.";
      } else if (error.response?.status === 404) {
        errorMessage = "User not found.";
      } else if (error.response?.status === 500) {
        errorMessage = error.response.data.error || "Server error occurred. Please try disabling the user instead.";
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Get user role display from backend data
  const getUserRole = (user) => {
    // Check if user has roles from backend
    if (user.CMS_UserRole && user.CMS_UserRole.length > 0) {
      // Get the first role's display name
      const primaryRole = user.CMS_UserRole[0];
      return primaryRole.CMS_Role?.RoleDisplayName || primaryRole.CMS_Role?.RoleName || 'User';
    }
    
    // Fallback: Check user permissions
    if (user.UserIsGlobalAdministrator) return 'Global Admin';
    if (user.UserIsEditor) return 'Editor';
    
    return 'User';
  };

  // Get user status from backend UserEnabled field
  const getUserStatus = (user) => {
    return user.UserEnabled ? 'Active' : 'Inactive';
  };
  
  // Get role chip color
  const getRoleColor = (roleName) => {
    const lowerRole = roleName.toLowerCase();
    if (lowerRole.includes('admin')) return 'error';
    if (lowerRole.includes('editor') || lowerRole.includes('manager')) return 'warning';
    return 'default';
  };

  // Get user initials for avatar
  const getUserInitials = (user) => {
    const firstName = user.FirstName || '';
    const lastName = user.LastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const columns = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 180,
      getActions: (params) => {
        if (!params.row.UserID) {
          return [];
        }
        return [
          <Tooltip key="profile edit" title="Edit User Profile" arrow placement="top">
          <span>
            <GridActionsCellItem
              icon={<ContactEmergency />}
              label="Edit User Profile"
              showInMenu={false}
              onClick={() => handleEditUserProfile(params.row.UserID)}
            />
          </span>
        </Tooltip>,
        <Tooltip key="edit" title="Edit User" arrow placement="top">
          <span>
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit User"
              showInMenu={false}
              onClick={() => handleEditUser(params.row.UserID)}
            />
          </span>
        </Tooltip>,
        <Tooltip key="reset-password" title="Reset Password" arrow placement="top">
          <span>
            <GridActionsCellItem
              icon={<ResetPasswordIcon />}
              label="Reset Password"
              showInMenu={false}
              onClick={() => handleResetPassword(params.row)}
            />
          </span>
        </Tooltip>,
        <Tooltip key="toggle-status" title={getUserStatus(params.row) === 'Active' ? 'Disable User' : 'Enable User'} arrow placement="top">
          <span>
            <GridActionsCellItem
              icon={getUserStatus(params.row) === 'Active' ? <DisableIcon /> : <ActiveIcon />}
              label={getUserStatus(params.row) === 'Active' ? 'Disable User' : 'Enable User'}
              showInMenu={false}
              onClick={() => handleDisableUser(params.row)}
            />
          </span>
        </Tooltip>,
        <Tooltip key="delete" title="Delete User" arrow placement="top">
          <span>
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete User"
              showInMenu={false}
              onClick={() => handleDeleteUser(params.row)}
            />
          </span>
        </Tooltip>,
      ];
      }
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: (params) => {
        // Prefer Azure URL from CMS_UserSettings.UserPicture, fallback to legacy MiddleName uploads path
        const rawUrl =
          params.row.UserPicture ||
          params.row?.CMS_UserSettings_CMS_UserSettings_UserSettingsUserIDToCMS_User?.UserPicture ||
          (typeof params.row.MiddleName === 'string' ? params.row.MiddleName : null);

        const isAbsoluteUrl = typeof rawUrl === 'string' && /^https?:\/\//i.test(rawUrl);
        const isLegacyUpload = typeof rawUrl === 'string' && rawUrl.startsWith('/uploads/');
        const imageUrl = isAbsoluteUrl ? rawUrl : isLegacyUpload ? `${getApiBaseUrl()}${rawUrl}` : null;
        const hasProfileImage = Boolean(imageUrl);
        
        return (
          <Box className={styles.nameCell}>
            <Avatar 
              src={imageUrl}
              className={hasProfileImage ? styles.userAvatarWithImage : styles.userAvatar}
              onError={(e) => {
                // If image fails to load, hide the src to show initials instead
                e.target.src = '';
              }}
            >
              {(!hasProfileImage || !imageUrl) && getUserInitials(params.row)}
            </Avatar>
            <Box className={styles.nameContainer}>
              <Typography variant="body2" fontWeight="medium" className={styles.userName}>
                {`${params.row.FirstName || ''} ${params.row.LastName || ''}`.trim() || 'N/A'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                @{params.row.UserName || 'N/A'}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.Email || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 180,
      sortable: false,
      renderCell: (params) => {
        const role = getUserRole(params.row);
        const roleColor = getRoleColor(role);
        return (
          <Chip
            label={role}
            color={roleColor}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const status = getUserStatus(params.row);
        const isActive = status === 'Active';
        return (
          <Chip
            icon={isActive ? <ActiveIcon /> : <InactiveIcon />}
            label={status}
            color={isActive ? 'success' : 'error'}
            size="small"
            variant="filled"
          />
        );
      },
    }
  ];

  if (isLoading) {
    return (
      <main>
        {/* Blue Header Banner */}
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">User Management</h1>
                <p>Manage user accounts, roles, and permissions</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <Box className={styles.loadingContainer}>
            <CircularProgress />
            <Typography className={styles.loadingText}>Loading users...</Typography>
          </Box>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main>
        {/* Blue Header Banner */}
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">User Management</h1>
                <p>Manage user accounts, roles, and permissions</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <Box className={styles.errorContainer}>
            <Alert severity="error">{error}</Alert>
          </Box>
        </div>
      </main>
    );
  }

  return (
    <main>
      {/* Blue Header Banner */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">User Management</h1>
              <p>Manage user accounts, roles, and permissions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="full-row white">
      <Box className={styles.container}>

      

      {/* Search and Controls */}
      <Paper className={styles.searchPaper}>
        {/* Create New User Button */}
      <Box className={styles.buttonContainer}>
        <Button
          variant="contained"
          onClick={() => navigate('/admin/users/create')}
          startIcon={<AddIcon />}
          className={styles.addButton}
        >
          Create New User
        </Button>
      </Box>
      
        <Box className={styles.searchContainer}>
          <TextField
            placeholder="Search users by name, email, or username"
            value={searchTerm}
            onChange={(e) => {
              const val = e.target.value;
              setSearchTerm(val);
              // When the field is cleared (e.g. backspaced to empty), reload the
              // full list — same as clicking the clear (✕) button.
              if (val === '' && searchTerm !== '') {
                handleClearSearch();
              }
            }}
            onKeyPress={handleSearchKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <ClearIcon 
                    className={styles.clearIcon}
                    onClick={handleClearSearch}
                  />
                </InputAdornment>
              ),
            }}
            className={styles.searchField}
          />
          <Button
            variant="contained"
            startIcon={isMobile ? undefined : <SearchIcon />}
            onClick={handleSearch}
            className={`${styles.searchButton} ${styles.iconSearchButton}`}
            aria-label="Search"
          >
            {isMobile ? <SearchIcon /> : "Search"}
          </Button>
        </Box>
      </Paper>

      {/* Data Grid */}
      <Paper className={styles.dataGridPaper}>
        <DataGrid
          rows={data}
          columns={columns}
          getRowId={(row) => row.UserID}
          loading={isLoading}

          // Server-side pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={pagination.totalCount}

          // Server-side sorting
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}

          autoHeight
          disableSelectionOnClick
          disableMultipleSelection
          disableColumnFilter
          disableDensitySelector
          hideFooterPagination
          slots={{
            toolbar: null
          }}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4, gap: 0.5 }}>
                <Typography sx={{ fontWeight: 600, color: '#21252C' }}>No users found</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Try a different search term.</Typography>
              </Box>
            ),
          }}
          className="usersDataGrid"
        />
      </Paper>

      {/* Custom Pagination */}
      <Paper className={styles.paginationPaper}>
        <Stack className={styles.paginationStack}>
          {/* Pagination Info */}
          <Typography variant="body2" color="text.secondary">
            Showing {data.length > 0 ? ((pagination.page - 1) * pagination.limit) + 1 : 0} to {Math.min(pagination.page * pagination.limit, pagination.totalCount)} of {pagination.totalCount} users
          </Typography>

          {/* Pagination Controls */}
          <Stack className={styles.paginationControls}>
            {/* Previous Button 
            <IconButton
              onClick={() => {
                if (pagination.page > 1) {
                  const newPage = pagination.page - 1;
                  setPaginationModel(prev => ({ ...prev, page: newPage - 1 }));
                  fetchUsersData(newPage, pagination.limit, searchTerm);
                }
              }}
              disabled={pagination.page <= 1 || isLoading}
              sx={{
                color: '#003063',
                '&:hover': { backgroundColor: 'rgba(0, 48, 99, 0.1)' },
                '&:disabled': { color: '#ccc' }
              }}
            >
              <PreviousIcon />
            </IconButton>*/}

            {/* Page Numbers */}
            <Pagination
              count={pagination.totalPages}
              page={pagination.page}
              onChange={(_, page) => {
                setPaginationModel(prev => ({ ...prev, page: page - 1 }));
                fetchUsersData(page, pagination.limit, searchTerm);
              }}
              disabled={isLoading}
              color="primary"
              variant="outlined"
              shape="rounded"
              showFirstButton
              showLastButton
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={1}
              className="usersPagination"
            />

            {/* Next Button 
            <IconButton
              onClick={() => {
                if (pagination.page < pagination.totalPages) {
                  const newPage = pagination.page + 1;
                  setPaginationModel(prev => ({ ...prev, page: newPage - 1 }));
                  fetchUsersData(newPage, pagination.limit, searchTerm);
                }
              }}
              disabled={pagination.page >= pagination.totalPages || isLoading}
              sx={{
                color: '#003063',
                '&:hover': { backgroundColor: 'rgba(0, 48, 99, 0.1)' },
                '&:disabled': { color: '#ccc' }
              }}
            >
              <NextIcon />
            </IconButton>
            */}
          </Stack>

          {/* Page Size Selector */}
          <Box className={styles.pageSizeSelector}>
            <Typography variant="body2" color="text.secondary">
              Show:
            </Typography>
            <TextField
              select
              size="small"
              value={pagination.limit}
              onChange={(e) => {
                const newLimit = parseInt(e.target.value);
                setPaginationModel(prev => ({ ...prev, pageSize: newLimit, page: 0 }));
                fetchUsersData(1, newLimit, searchTerm);
              }}
              SelectProps={{
                native: true,
              }}
              className={`${styles.pageSizeField} pageSizeField`}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </TextField>
            <Typography variant="body2" color="text.secondary">
              per page
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog 
        open={confirmDialog.open} 
        onClose={() => setConfirmDialog({ open: false, action: '', user: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className={styles.dialogTitle}>
          {confirmDialog.action === 'resetPassword' && 'Reset User Password'}
          {confirmDialog.action === 'disable' && (confirmDialog.user?.UserEnabled ? 'Disable User' : 'Enable User')}
          {confirmDialog.action === 'delete' && 'Delete User'}
        </DialogTitle>
        <DialogContent>
          {confirmDialog.action === 'resetPassword' && (
            <Box>
              <Typography variant="body1" className={styles.dialogText}>
                Are you sure you want to reset the password for:
              </Typography>
              <Box className={styles.userInfoBox}>
                <Typography variant="body2" className={styles.userInfoName}>
                  <strong>Name:</strong> {confirmDialog.user?.FirstName} {confirmDialog.user?.LastName}
                </Typography>
                <Typography variant="body2" className={styles.userInfoEmail}>
                  <strong>Email:</strong> {confirmDialog.user?.Email || confirmDialog.user?.UserName}
                </Typography>
              </Box>
              <Alert severity="info" className={styles.alertInfo}>
                A password reset link will be sent to the user's email address. The user will receive instructions to create a new password.
              </Alert>
            </Box>
          )}
          {confirmDialog.action === 'disable' && (
            <Box>
              <Typography variant="body1" className={styles.dialogText}>
                Are you sure you want to {confirmDialog.user?.UserEnabled ? 'disable' : 'enable'} this user?
              </Typography>
              <Box className={styles.userInfoBox}>
                <Typography variant="body2" className={styles.userInfoName}>
                  <strong>Name:</strong> {confirmDialog.user?.FirstName} {confirmDialog.user?.LastName}
                </Typography>
                <Typography variant="body2" className={styles.userInfoEmail}>
                  <strong>Email:</strong> {confirmDialog.user?.Email || confirmDialog.user?.UserName}
                </Typography>
              </Box>
              {confirmDialog.user?.UserEnabled && (
                <Alert severity="warning">
                  Disabling this user will prevent them from logging in to the system.
                </Alert>
              )}
            </Box>
          )}
          {confirmDialog.action === 'delete' && (
            <Box>
              <Typography variant="body1" className={styles.dialogText}>
                Are you sure you want to permanently delete this user?
              </Typography>
              <Box className={styles.userInfoBox}>
                <Typography variant="body2" className={styles.userInfoName}>
                  <strong>Name:</strong> {confirmDialog.user?.FirstName} {confirmDialog.user?.LastName}
                </Typography>
                <Typography variant="body2" className={styles.userInfoEmail}>
                  <strong>Email:</strong> {confirmDialog.user?.Email || confirmDialog.user?.UserName}
                </Typography>
              </Box>
              <Alert severity="error">
                <strong>Warning:</strong> This action cannot be undone. All user data will be permanently deleted.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button 
            onClick={() => setConfirmDialog({ open: false, action: '', user: null })}
            variant="outlined"
            className={styles.cancelButton}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmAction} 
            variant="contained"
            color={confirmDialog.action === 'delete' ? 'error' : 'primary'}
            className={confirmDialog.action === 'delete' ? styles.confirmButtonDelete : styles.confirmButton}
          >
            {confirmDialog.action === 'resetPassword' && 'Send Reset Link'}
            {confirmDialog.action === 'disable' && (confirmDialog.user?.UserEnabled ? 'Disable User' : 'Enable User')}
            {confirmDialog.action === 'delete' && 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          className={styles.snackbarAlert}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
      </div>
    </main>
  );
};



export default Users


